if (!customElements.get('faq-component')) {
	customElements.define(
		'faq-component',
		class FAQ extends HTMLElement {
			constructor() {
				super();
				
				// State
				this.faq = null;
				this.searchQuery = '';
				this.desktopSectionIdx = 0;
				this.isSearchOpen = false;
				
				// DOM elements
				this.searchInput = null;
				this.searchResults = null;
				this.clearButton = null;
				this.desktopSectionButtons = null;
				this.mobileSectionButtons = null;
				this.faqItems = null;
			}

			connectedCallback() {
				this.initializeData();
				this.setupEventListeners();
				this.initializeState();
				this.handleHashNavigation();
			}

			initializeData() {
				// Get FAQ data from JSON script
				const dataScript = this.querySelector('#faq-data');
				if (dataScript) {
					try {
						this.faq = JSON.parse(dataScript.textContent);
						// Initialize open state for all FAQ entries
						if (this.faq && this.faq.sections) {
							this.faq.sections.forEach(section => {
								if (section.entries) {
									section.entries.forEach(entry => {
										entry.open = false; // Initialize all as closed
									});
								}
							});
						}
					} catch (error) {
						console.error('Error parsing FAQ data:', error);
					}
				}
			}

			setupEventListeners() {
				// Search input
				this.searchInput = this.querySelector('.search-input');
				if (this.searchInput) {
					this.searchInput.addEventListener('input', this.handleSearchInput.bind(this));
					this.searchInput.addEventListener('focus', this.handleSearchFocus.bind(this));
					this.searchInput.addEventListener('blur', this.handleSearchBlur.bind(this));
					this.searchInput.addEventListener('keydown', this.handleSearchKeydown.bind(this));
				}

				// Clear search button
				this.clearButton = this.querySelector('.clear-search');
				if (this.clearButton) {
					this.clearButton.addEventListener('click', this.clearSearch.bind(this));
				}

				// Desktop section buttons
				this.desktopSectionButtons = this.querySelectorAll('.desktop-section-btn');
				this.desktopSectionButtons.forEach((btn, idx) => {
					btn.addEventListener('click', () => this.selectDesktopSection(idx));
				});

				// Mobile section buttons
				this.mobileSectionButtons = this.querySelectorAll('.mobile-section-btn');
				this.mobileSectionButtons.forEach((btn, idx) => {
					btn.addEventListener('click', () => this.toggleMobileSection(idx));
				});

				// FAQ item buttons
				this.faqItems = this.querySelectorAll('.faq-item');
				this.faqItems.forEach((item) => {
					const button = item.querySelector('.faq-item-btn');
					if (button) {
						button.addEventListener('click', (e) => {
							const sectionIdx = parseInt(e.currentTarget.dataset.sectionIdx);
							const itemIdx = parseInt(e.currentTarget.dataset.itemIdx);
							this.toggleFAQItem(sectionIdx, itemIdx);
						});
					}
				});

				// Search results container
				this.searchResults = this.querySelector('.search-results');
			}

			initializeState() {
				// Initialize first section as active
				this.updateDesktopSections();
				this.updateMobileSections();
			}

			handleSearchInput(e) {
				this.searchQuery = e.target.value;
				this.updateSearchResults();
				this.updateClearButton();
			}

			handleSearchFocus() {
				this.isSearchOpen = true;
				this.updateSearchResults();
			}

			handleSearchBlur() {
				// Delay hiding to allow clicking on results
				setTimeout(() => {
					this.isSearchOpen = false;
					this.updateSearchResults();
				}, 200);
			}

			handleSearchKeydown(e) {
				if (e.key === 'Escape') {
					this.clearSearch();
				}
			}

			updateSearchResults() {
				if (!this.searchResults || !this.faq) return;

				if (!this.isSearchOpen || this.searchQuery.length < 2) {
					this.searchResults.style.display = 'none';
					return;
				}

				const suggestions = this.getSuggestions();
				const filteredSuggestions = suggestions.filter(item => 
					item.question.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
					item.answer_text.toLowerCase().includes(this.searchQuery.toLowerCase())
				);

				if (filteredSuggestions.length === 0) {
					this.searchResults.style.display = 'none';
					return;
				}

				this.searchResults.innerHTML = filteredSuggestions.map(item => `
					<div class="search-result-item cursor-pointer hover:bg-gray-50" data-section-idx="${item.sectionIndex}" data-item-idx="${item.index}">
						<div class="px-5 py-4 block text-start">
							<div class="font-medium">${this.highlightText(item.question, this.searchQuery)}</div>
							<div class="text-sm text-gray-600">${this.highlightText(this.truncate(item.answer_text), this.searchQuery)}</div>
						</div>
					</div>
				`).join('');

				// Add click listeners to search results
				const resultItems = this.searchResults.querySelectorAll('.search-result-item');
				resultItems.forEach(item => {
					item.addEventListener('click', () => {
						const sectionIdx = parseInt(item.dataset.sectionIdx);
						const itemIdx = parseInt(item.dataset.itemIdx);
						this.onSelectItem(sectionIdx, itemIdx);
					});
				});

				this.searchResults.style.display = 'block';
			}

			updateClearButton() {
				if (this.clearButton) {
					this.clearButton.style.display = this.searchQuery ? 'block' : 'none';
				}
			}

			clearSearch() {
				this.searchQuery = '';
				if (this.searchInput) {
					this.searchInput.value = '';
				}
				this.updateSearchResults();
				this.updateClearButton();
				if (this.searchInput) {
					this.searchInput.focus();
				}
			}

			getSuggestions() {
				if (!this.faq) return [];
				
				const suggestions = [];
				this.faq.sections.forEach((section, sectionIndex) => {
					section.entries.forEach((item, itemIndex) => {
						suggestions.push({
							...item,
							index: itemIndex,
							sectionIndex: sectionIndex,
							sectionTitle: section.title
						});
					});
				});
				return suggestions;
			}

			highlightText(text, query) {
				if (!query) return text;
				const regex = new RegExp(`(${query})`, 'gi');
				return text.replace(regex, '<strong>$1</strong>');
			}

			truncate(text) {
				return text.length > 255 ? text.slice(0, 255) + '…' : text;
			}

			selectDesktopSection(idx) {
				this.desktopSectionIdx = idx;
				this.updateDesktopSections();
				this.updateMobileSections();
				if (this.faq && this.faq.sections[idx]) {
					window.location.hash = this.faq.sections[idx].handle;
				}
			}

			toggleMobileSection(idx) {
				if (this.faq && this.faq.sections[idx]) {
					this.faq.sections[idx].show_mobile = !this.faq.sections[idx].show_mobile;
					this.updateMobileSections();
				}
			}

			toggleFAQItem(sectionIdx, itemIdx) {
				if (this.faq && this.faq.sections[sectionIdx] && this.faq.sections[sectionIdx].entries[itemIdx]) {
					this.faq.sections[sectionIdx].entries[itemIdx].open = !this.faq.sections[sectionIdx].entries[itemIdx].open;
					this.updateFAQItems();
				}
			}

			onSelectItem(sectionIdx, itemIdx) {
				this.desktopSectionIdx = sectionIdx;
				if (this.faq && this.faq.sections[sectionIdx]) {
					this.faq.sections[sectionIdx].show_mobile = true;
					if (this.faq.sections[sectionIdx].entries[itemIdx]) {
						this.faq.sections[sectionIdx].entries[itemIdx].open = true;
					}
				}
				
				this.updateDesktopSections();
				this.updateMobileSections();
				this.updateFAQItems();
				
				// Scroll to the selected item
				const targetElement = this.querySelector(`[data-faq-ref="faq-${sectionIdx}-q-${itemIdx}"]`);
				if (targetElement) {
					const yOffset = -150;
					const y = targetElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
					window.scrollTo({ top: y, behavior: 'smooth' });
				}
				
				this.clearSearch();
			}

			updateDesktopSections() {
				this.desktopSectionButtons.forEach((btn, idx) => {
					const isActive = idx === this.desktopSectionIdx;
					btn.classList.toggle('text-primary-900', isActive);
					btn.classList.toggle('text-dark-600', !isActive);
					btn.classList.toggle('hover:text-dark-900', !isActive);
					
					const indicator = btn.querySelector('.h-0\\.5');
					if (indicator) {
						indicator.classList.toggle('hidden', !isActive);
					}
				});
			}

			updateMobileSections() {
				const mobileSections = this.querySelectorAll('.mobile-section');
				
				this.mobileSectionButtons.forEach((btn, idx) => {
					const isOpen = this.faq && this.faq.sections[idx] && this.faq.sections[idx].show_mobile;
					btn.classList.toggle('text-primary-900', isOpen);
					btn.classList.toggle('text-dark-900', !isOpen);
					btn.classList.toggle('hover:text-dark-600', !isOpen);
					
					const icon = btn.querySelector('.section-icon');
					if (icon) {
						icon.classList.toggle('rotate-90', isOpen);
					}
				});

				mobileSections.forEach((section, idx) => {
					const isOpen = this.faq && this.faq.sections[idx] && this.faq.sections[idx].show_mobile;
					const isDesktopActive = idx === this.desktopSectionIdx;
					
					section.classList.toggle('block', isOpen);
					section.classList.toggle('hidden', !isOpen);
					section.classList.toggle('sm:block', isDesktopActive);
					section.classList.toggle('sm:hidden', !isDesktopActive);
				});
			}

			updateFAQItems() {
				this.faqItems.forEach(item => {
					const sectionIdx = parseInt(item.dataset.sectionIdx);
					const itemIdx = parseInt(item.dataset.itemIdx);
					
					// Check if the specific entry is open (explicitly check for true)
					const isOpen = this.faq && 
						this.faq.sections[sectionIdx] && 
						this.faq.sections[sectionIdx].entries[itemIdx] && 
						this.faq.sections[sectionIdx].entries[itemIdx].open === true;
					
					// Toggle the active class based on the explicit open state
					item.classList.toggle('active', isOpen);
				});
			}

			handleHashNavigation() {
				if (window.location.hash && this.faq) {
					const handle = window.location.hash.replace('#', '');
					const selectedSectionIndex = this.faq.sections.findIndex(section => section.handle === handle);
					if (selectedSectionIndex >= 0) {
						this.faq.sections[selectedSectionIndex].show_mobile = true;
						this.desktopSectionIdx = selectedSectionIndex;
						this.updateDesktopSections();
						this.updateMobileSections();
						
						// Scroll to first item in section
						setTimeout(() => {
							const targetElement = this.querySelector(`[data-faq-ref="faq-${selectedSectionIndex}-q-0"]`);
							if (targetElement) {
								const yOffset = -150;
								const y = targetElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
								window.scrollTo({ top: y, behavior: 'smooth' });
							}
						}, 100);
					}
				}
			}
		}
	);
}